<i18n>
{
  "de": {
    "from": "ab",
    "soldOut": "Ausverkauft",
    "limited": "{count} verfügbar",
    "limitedStock": "Limitiert",
    "purchased": "Gekauft",
    "pending": "Ausstehend",
    "purchase": "Kaufen",
    "buyAgain": "Nochmal kaufen",
    "comingSoon": "Bald verfügbar",
    "draft": "Entwurf",
    "scheduled": "Geplant"
  },
  "en": {
    "from": "from",
    "soldOut": "Sold out",
    "limited": "{count} available",
    "limitedStock": "Limited",
    "purchased": "Purchased",
    "pending": "Pending",
    "purchase": "Purchase",
    "buyAgain": "Buy again",
    "comingSoon": "Coming soon",
    "draft": "Draft",
    "scheduled": "Scheduled"
  }
}
</i18n>

<template>
  <article
    class="product-card"
    :class="{
      'product-card--sold': product.isSold,
      'product-card--pending': product.isPending,
      'product-card--sold-out': isSoldOut,
      'product-card--draft': product.status === 'draft',
      'product-card--scheduled': product.status === 'scheduled'
    }"
  >
    <!-- Status Badge -->
    <div
      v-if="statusBadge"
      class="product-card__badge"
      :class="`product-card__badge--${statusBadge.type}`"
    >
      {{ statusBadge.text }}
    </div>

    <!-- Image -->
    <div class="product-card__image-wrapper">
      <img
        v-if="productImage"
        :src="productImage"
        :alt="product.name"
        class="product-card__image"
        loading="lazy"
      >
      <div
        v-else
        class="product-card__image product-card__image--placeholder"
      />
      <!-- Stock indicator -->
      <div
        v-if="isLimitedStock && !isSoldOut"
        class="product-card__stock"
      >
        <span v-if="product.showRemainingStock">{{ $t('limited', { count: product.stockRemaining }) }}</span>
        <span v-else>{{ $t('limitedStock') }}</span>
      </div>
      <!-- Categories overlay -->
      <div
        v-if="productCategories.length"
        class="product-card__categories"
      >
        <nuxt-link
          v-for="category in productCategories"
          :key="category._id"
          :to="localePath({ name: 'id-shop-products-category', params: { id: artist.urlAlias, category: category.slug } })"
          class="product-card__category-tag"
        >
          {{ category.name }}
        </nuxt-link>
      </div>
    </div>

    <!-- Content -->
    <div class="product-card__content">
      <!-- Title -->
      <h3 class="product-card__title">
        {{ product.name }}
      </h3>

      <!-- Description (truncated) -->
      <div
        v-if="truncatedDescription"
        class="product-card__description"
        v-html="truncatedDescription"
      />

      <!-- Footer: Price & Action -->
      <div class="product-card__footer">
        <div class="product-card__price">
          <span
            v-if="product.minAmount"
            class="product-card__price-from"
          >{{ $t('from') }}</span>
          <span class="product-card__price-amount">{{ centToCurrency(displayPrice, 1, 'EUR') }}</span>
        </div>
        <a
          v-if="canPurchase"
          :href="checkoutUrl(product._id)"
          class="product-card__button product-card__button--on-product"
        >
          {{ product.isSold ? $t('buyAgain') : $t('purchase') }}
        </a>
        <span
          v-else-if="isSoldOut"
          class="product-card__button product-card__button--sold-out"
        >
          {{ $t('soldOut') }}
        </span>
        <span
          v-else-if="product.isPending"
          class="product-card__button product-card__button--pending"
        >
          {{ $t('pending') }}
        </span>
        <span
          v-else
          class="product-card__button product-card__button--disabled"
        >
          {{ $t('comingSoon') }}
        </span>
      </div>
    </div>
  </article>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'ProductCard',

  props: {
    product: {
      type: Object,
      required: true
    }
  },

  computed: {
    ...mapState(['artist']),

    productImage() {
      if (!this.artist || !this.product || !this.product.image) {
        return ''
      }

      // Legacy string format
      if (typeof this.product.image === 'string') {
        return this.product.image
      }

      // Standard image object - smaller size for compact cards
      return `/services/media/image/${this.artist._id}/${this.product.image.name}/600/0/image.webp`
    },

    displayPrice() {
      return this.product.minAmount > 0 ? this.product.minAmount : this.product.amount
    },

    isLimitedStock() {
      return Number.isInteger(this.product.stockRemaining)
    },

    isSoldOut() {
      return this.product.stockRemaining === 0
    },

    statusBadge() {
      if (this.product.isSold && !this.isSoldOut) {
        return { type: 'purchased', text: this.$t('purchased') }
      }
      if (this.product.isPending) {
        return { type: 'pending', text: this.$t('pending') }
      }
      if (this.isSoldOut) {
        return { type: 'sold-out', text: this.$t('soldOut') }
      }
      if (this.product.status === 'draft') {
        return { type: 'draft', text: this.$t('draft') }
      }
      if (this.product.status === 'scheduled') {
        return { type: 'scheduled', text: this.$t('scheduled') }
      }
      return null
    },

    truncatedDescription() {
      if (!this.product.description) return ''
      const text = this.stripHtml(this.product.description)
      if (text.length > 100) {
        return text.substring(0, 100) + '...'
      }
      return text
    },

    canPurchase() {
      return (
        this.product.status === 'published' &&
        !this.isSoldOut &&
        !this.product.isPending &&
        this.artist?.isBoarded
      )
    },

    productCategories() {
      return this.product.categories || []
    }
  }
}
</script>

<style lang="scss" scoped>
@import "assets/sass/_variables";

.product-card {
  position: relative;
  background-color: var(--product-background-color, #1e1b26);
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  flex-direction: column;
  height: 100%; // Stretch to match tallest card in grid row

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }

  // Status modifiers
  &--sold {
    border: 2px solid var(--primary-color, #4CAF50);
  }

  &--pending {
    opacity: 0.85;
  }

  &--sold-out {
    opacity: 0.6;
    pointer-events: none;
  }

  &--draft,
  &--scheduled {
    opacity: 0.6;
  }

  // Badge
  &__badge {
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 3;
    padding: 3px 8px;
    border-radius: 12px;
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;

    &--purchased {
      background: var(--primary-color, #4CAF50);
      color: var(--primary-text-color, #fff);
    }

    &--pending {
      background: #FF9800;
      color: #fff;
    }

    &--sold-out {
      background: #f44336;
      color: #fff;
    }

    &--draft {
      background: #9E9E9E;
      color: #fff;
    }

    &--scheduled {
      background: #2196F3;
      color: #fff;
    }
  }

  // Image - square 1:1 aspect ratio with center-top alignment
  &__image-wrapper {
    position: relative;
    overflow: hidden;
    aspect-ratio: 1 / 1; // Square aspect ratio
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center top; // Align image center horizontally, top vertically
    display: block;
    transition: transform 0.3s ease;
    // Softer fade mask for product cards
    -webkit-mask-image: -webkit-gradient(
      linear,
      left top,
      left bottom,
      color-stop(0, rgba(0, 0, 0, 1)),
      color-stop(0.85, rgba(0, 0, 0, 1)),
      color-stop(0.95, rgba(0, 0, 0, 0.5)),
      color-stop(1, rgba(0, 0, 0, 0))
    );
    mask-image: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 1) 0%,
      rgba(0, 0, 0, 1) 85%,
      rgba(0, 0, 0, 0.5) 95%,
      rgba(0, 0, 0, 0) 100%
    );

    // Placeholder styling when no image
    &--placeholder {
      background-color: var(--product-background-color, #1e1b26);
    }
  }

  &:hover &__image {
    transform: scale(1.05);
  }

  // Stock indicator
  &__stock {
    position: absolute;
    bottom: 8px;
    right: 8px;
    background: rgba(0, 0, 0, 0.7);
    color: #fff;
    padding: 3px 8px;
    border-radius: 10px;
    font-size: 0.65rem;
    font-weight: 600;
    z-index: 2;
  }

  // Content
  &__content {
    padding: 14px;
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: var(--product-background-color, #1e1b26);
    container-type: inline-size; // Enables card-width container queries for the footer
  }

  // Title
  &__title {
    font-size: 1.05rem;
    font-weight: 700;
    color: var(--product-background-text-color, #fff);
    margin: 0 0 10px 0;
    line-height: 1.3;
    // Truncate to 2 lines
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  // Categories overlay (positioned bottom-left of image)
  &__categories {
    position: absolute;
    bottom: 8px;
    left: 8px;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    max-width: calc(100% - 16px);
    z-index: 2;
  }

  &__category-tag {
    display: inline-block;
    padding: 3px 8px;
    background: color-mix(in hsl, var(--product-background-color), var(--product-background-text-color) 10%);
    border-radius: 10px;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--product-background-text-color, #fff);
    border: 2px solid color-mix(in hsl, var(--product-background-color), var(--product-background-text-color) 20%);
    text-decoration: none;
    transition: background 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);

    &:hover {
      color: var(--product-background-color, #1e1b26);
      background: color-mix(in hsl, var(--product-background-text-color), var(--product-background-color) 10%);
      border: 2px solid color-mix(in hsl, var(--product-background-text-color), var(--product-background-color) 20%);
    }
  }

  // Description
  &__description {
    font-size: 0.85rem;
    color: var(--product-background-text-color, #ccc);
    line-height: 1.5;
    flex: 1;
    margin-bottom: 14px;
    // Truncate to 2 lines for tighter look
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  // Footer
  &__footer {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    flex-wrap: wrap; // Fallback for browsers without container queries
    padding-top: 14px;
    margin-top: auto;

    // Theme-aware border using pseudo-element
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: var(--product-background-text-color, #fff);
      opacity: 0.15;
    }
  }

  // Price
  &__price {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  &__price-from {
    font-size: 0.7rem;
    color: var(--product-background-text-color, #aaa);
    opacity: 0.8;
    order: -1; // Show "from" above the amount
  }

  &__price-amount {
    font-size: 1.25rem;
    font-weight: 800;
    color: var(--product-background-text-color, #fff);
    line-height: 1.2;
  }

  &__button {
    display: block;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 600;
    text-align: center;
    text-decoration: none;
    transition: all 0.2s ease;
    cursor: pointer;
    border: none;

    &--on-product {
      background: var(--product-background-text-color, #fff);
      color: var(--product-background-color, #fff);

      &:hover {
        opacity: 0.9;
      }
    }

    &--primary {
      background: var(--primary-color, #333);
      color: var(--primary-text-color, #fff);

      &:hover {
        opacity: 0.9;
      }
    }

    &--sold-out {
      background: rgba(244, 67, 54, 0.2);
      color: #f44336;
      cursor: not-allowed;
    }

    &--pending {
      background: rgba(255, 152, 0, 0.2);
      color: #FF9800;
      cursor: default;
    }

    &--disabled {
      background: rgba(255, 255, 255, 0.1);
      color: var(--product-background-text-color, #999);
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

// Narrow cards (e.g. two-column mobile grid): stack price above a full-width button
@container (max-width: 239px) {
  .product-card__footer {
    flex-direction: column;
    align-items: stretch;
  }

  .product-card__price {
    flex-direction: row;
    align-items: baseline;
    gap: 5px;
  }

  .product-card__button {
    width: 100%;
  }
}

// Mobile adjustments - keep fonts readable, don't shrink too much
@media screen and (max-width: #{$mobile}) {
  .product-card {
    &__content {
      padding: 12px;
    }

    &__title {
      font-size: 1rem; // Keep readable on mobile
      margin-bottom: 8px;
    }

    &__description {
      font-size: 0.8rem;
      margin-bottom: 12px;
    }

    &__price-amount {
      font-size: 1.15rem;
    }

    &__footer {
      padding-top: 12px;
    }

    &__button {
      padding: 8px 14px; // Good touch target
      font-size: 0.8rem;
    }

    &__badge {
      font-size: 0.6rem;
      padding: 3px 8px;
    }

    &__stock {
      font-size: 0.65rem;
      padding: 3px 8px;
    }
  }
}
</style>
